import type { Translate } from "../i18n/translate";

/**
 * What a failed login actually was, and what to say about it.
 *
 * ## Why this is not just `status === 401 ? bad password : server is down`
 *
 * It used to be exactly that, and the else-branch swallowed everything —
 * including **429**. So a rate limit, which is temporary, self-healing, and
 * arrives with the exact number of seconds to wait, was shown as *"Can't reach
 * the server. It may be restarting, or offline."*
 *
 * That message is worse than merely wrong. It tells the reader the server is
 * down, so they retry; every retry keeps the window open; and the condition
 * sustains itself. A person who waits 45 seconds gets in, and a person who is
 * told the server is offline never waits. The one time this fired in
 * production, it read as an outage during a deploy — which is the most
 * expensive possible moment to be told something false.
 *
 * `AuthContext`'s own comment already argues the mirror image of this: calling
 * a dead server "invalid password" sends someone hunting for a problem that is
 * not theirs. Same principle, third case.
 */

export type LoginFailureKind =
  | "invalid_credentials"
  | "owner_disabled"
  | "rate_limited"
  | "server_unreachable";

export interface LoginFailure {
  kind: LoginFailureKind;
  /** Seconds to wait, when the server told us. Null when it did not. */
  retryAfter: number | null;
}

/**
 * How long to wait, from whichever header the server actually sent.
 *
 * `@fastify/rate-limit` sets `retry-after` on a 429 and `x-ratelimit-reset` on
 * every response; both are in seconds. The body's prose ("retry in 45 seconds")
 * is the last resort, because a message is a UI string and may be reworded,
 * whereas the headers are the contract.
 */
export function retryAfterSeconds(
  headers: { get(name: string): string | null },
  message?: string
): number | null {
  for (const name of ["retry-after", "x-ratelimit-reset"]) {
    const raw = Number(headers.get(name));
    if (Number.isFinite(raw) && raw > 0) return Math.ceil(raw);
  }
  const parsed = /(\d+)\s*second/i.exec(message ?? "");
  return parsed ? Number(parsed[1]) : null;
}

/**
 * Classify a non-OK login response. Reads the body only for a 403
 * (`owner_disabled`) and a 429 (a retry hint the headers did not carry).
 */
export async function readLoginFailure(res: {
  status: number;
  headers: { get(name: string): string | null };
  json(): Promise<unknown>;
}): Promise<LoginFailure> {
  if (res.status === 401) {
    return { kind: "invalid_credentials", retryAfter: null };
  }
  if (res.status === 403) {
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error === "owner_disabled") {
        return { kind: "owner_disabled", retryAfter: null };
      }
    } catch {
      // Not the refusal we know; fall through to "unreachable".
    }
    return { kind: "server_unreachable", retryAfter: null };
  }
  if (res.status === 429) {
    let message: string | undefined;
    try {
      message = ((await res.json()) as { message?: string })?.message;
    } catch {
      // A rate limiter that answers without a body is still a rate limiter.
    }
    return {
      kind: "rate_limited",
      retryAfter: retryAfterSeconds(res.headers, message),
    };
  }
  return { kind: "server_unreachable", retryAfter: null };
}

/** The sentence the login form shows. */
export function loginErrorMessage(t: Translate, failure: LoginFailure): string {
  switch (failure.kind) {
    case "invalid_credentials":
      return t("shell:login.failure.invalidCredentials");
    case "owner_disabled":
      return t("shell:login.failure.ownerDisabled");
    case "rate_limited":
      return failure.retryAfter
        ? t("shell:login.failure.rateLimitedFor", {
            count: failure.retryAfter,
          })
        : t("shell:login.failure.rateLimited");
    case "server_unreachable":
      return t("shell:login.failure.serverUnreachable");
  }
}
